import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSessionStorageState } from '../../hooks/useSessionStorageState';
import { lookupOrder } from './orderLookupService';
import { saveProofToken } from '../MyPage/proofAccess';
import TrackingPanel from '../../components/tracking/TrackingPanel';
import PaymentPanel from '../../components/payment/PaymentPanel';
import OrderTermsInfo from '../../components/order/OrderTermsInfo';
import {
  joinLookupPhone,
  lookupFormSchema,
  LookupFormSchema,
  ORDER_NO_EXAMPLE,
  toOrderNo,
} from './lookupSchema';
import { Order, OrderStatus, ORDER_STATUS_LABEL, WorkStatus, WORK_STATUS_LABEL } from '../../types/order';
import styles from './Lookup.module.scss';

const MAX_FAIL_COUNT = 5;

interface Stage {
  eyebrow: string;
  label: string;
  statuses: OrderStatus[];
}

const STAGES: Stage[] = [
  { eyebrow: 'STEP 01', label: '주문 접수 · 결제', statuses: ['PENDING_QUOTE', 'QUOTE_CONFIRMED', 'PENDING_PAYMENT', 'PAYMENT_COMPLETED'] },
  { eyebrow: 'STEP 02', label: '시안 확인', statuses: ['PROOF_IN_PROGRESS', 'PROOF_FEEDBACK', 'PROOF_APPROVED'] },
  { eyebrow: 'STEP 03', label: '제작 · 생산', statuses: ['PRODUCTION'] },
  { eyebrow: 'STEP 04', label: '배송 · 완료', statuses: ['SHIPPED', 'COMPLETED'] },
];

function getStageIndex(status: OrderStatus): number {
  return STAGES.findIndex((stage) => stage.statuses.includes(status));
}

function maskPhone(phone: string): string {
  const digits = phone.replace(/[^0-9]/g, '');
  if (digits.length < 8) return phone;
  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
}

function formatDate(value: string): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '');
}

function formatWon(value: number): string {
  return `₩ ${value.toLocaleString('ko-KR')}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ko-KR');
}

// 주문 요약 카드의 작업 상태 배지 색 (대기 주황 / 작업중 파랑 / 완료 초록)
const WORK_BADGE_CLASS: Record<WorkStatus, string> = {
  WAITING: styles.badgeWait,
  IN_PROGRESS: styles.badgeStatus,
  COMPLETED: styles.badgeDone,
};

function Lookup() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LookupFormSchema>({
    resolver: zodResolver(lookupFormSchema),
  });
  const [failCount, setFailCount] = useSessionStorageState('lookupFailCount', 0);
  const [result, setResult] = useSessionStorageState<Order | null>('lookupResult', null);
  const [notFound, setNotFound] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const isLocked = failCount >= MAX_FAIL_COUNT;

  const onSubmit = async (values: LookupFormSchema) => {
    setNotFound(false);
    setServerError(null);

    try {
      const order = await lookupOrder({
        orderNo: toOrderNo(values.orderNo),
        name: values.name,
        phone: joinLookupPhone(values),
        password: values.password,
      });

      if (order) {
        if (order.proofToken) {
          saveProofToken(order.id, order.proofToken);
        }
        setResult(order);
        setFailCount(0);
      } else {
        setResult(null);
        setNotFound(true);
        setFailCount((count) => count + 1);
      }
    } catch (err) {
      setServerError(err instanceof Error ? err.message : '주문 조회 중 오류가 발생했습니다.');
    }
  };

  const resetLookup = () => {
    setResult(null);
    setNotFound(false);
    setServerError(null);
  };

  const stageIndex = result && result.status !== 'CANCELLED' ? getStageIndex(result.status) : -1;
  const hasGoods = Boolean(result?.title && result?.shape);

  return (
    <div className={styles.page}>
      {/* PAGE HEAD */}
      <div className={styles.pageHeadWrap}>
        <section className={styles.pageHead}>
          <div className={styles.crumb}>
            <Link to="/">HOME</Link>
            <svg viewBox="0 0 24 24">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span className={styles.crumbHere}>주문조회</span>
          </div>
          {/* <span className={styles.eyebrow}>ORDER LOOKUP</span> */}
          <h1 className={styles.pageTitle}>
            Order <em>Lookup</em>
          </h1>
          <p className={styles.pageDesc}>
            견적 문의 후 발급받은 <strong>주문번호</strong>와 <strong>비밀번호</strong>로 제작 · 배송 상태를 확인할 수
            있습니다.
          </p>
        </section>
      </div>

      {!result && (
        <>
          {/* MODE SELECT */}
          <section className={styles.modeArea}>
            <div className={styles.modeInner}>
              <div className={styles.modeGrid}>
                <div className={`${styles.modeCard} ${styles.active}`}>
                  <div className={styles.modeIcon}>
                    <svg viewBox="0 0 24 24">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21a8 8 0 0 1 16 0" />
                    </svg>
                  </div>
                  <div className={styles.modeBody}>
                    <div className={styles.modeTitle}>비회원 주문조회</div>
                    <div className={styles.modeSub}>
                      회원가입 없이 견적 문의 시 발급된 주문번호와 비밀번호로 조회합니다.
                    </div>
                  </div>
                  <span className={styles.modeCheck}>✓</span>
                </div>
                <Link to="/login" className={`${styles.modeCard}`}>
                  <div className={styles.modeIcon}>
                    <svg viewBox="0 0 24 24">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="m3 8 9 6 9-6" />
                    </svg>
                  </div>
                  <div className={styles.modeBody}>
                    <div className={styles.modeTitle}>회원 로그인 조회</div>
                    <div className={styles.modeSub}>회원가입 후 로그인하면 마이페이지에서 모든 주문을 모아볼 수 있습니다.</div>
                  </div>
                </Link>
              </div>
            </div>
          </section>

          {/* FORM */}
          <section className={styles.formWrap}>
            <div className={styles.formInner}>
              <div className={styles.formCard}>
                {isLocked ? (
                  <p className={styles.lockedNotice} role="alert">
                    5회 이상 조회에 실패하여 조회가 제한되었습니다. 고객센터로 문의해주세요.
                  </p>
                ) : (
                  <>
                    <h3>조회 정보를 입력해주세요</h3>
                    <p className={styles.formDesc}>입력하신 정보로 견적 문의 시 등록한 주문 건이 즉시 조회됩니다.</p>
  
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className={styles.fieldGrid}>
                        {/* 주문번호(필수): 해당 주문번호의 주문을 조회 */}
                        <div className={`${styles.field} ${styles.fieldFull}`}>
                          <label htmlFor="orderNo">
                            주문번호 <span className={styles.req}>*</span>
                          </label>
                          <input
                            id="orderNo"
                            className={styles.textInput}
                            placeholder={`예) ${ORDER_NO_EXAMPLE}`}
                            autoComplete="off"
                            autoCapitalize="characters"
                            {...register('orderNo')}
                          />
                          <p className={styles.fieldHint}>
                            견적 문의 접수 시 안내받은 주문번호 8자리(영문 2자리 + 숫자 6자리)를 입력해주세요.
                          </p>
                          {errors.orderNo && <p className={styles.fieldError}>{errors.orderNo.message}</p>}
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="name">
                            주문자 성함 <span className={styles.req}>*</span>
                          </label>
                          <input id="name" className={styles.textInput} placeholder="예) 김윤서" {...register('name')} />
                          {errors.name && <p className={styles.fieldError}>{errors.name.message}</p>}
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="phone1">
                            연락처 <span className={styles.req}>*</span>
                          </label>
                          <div className={styles.phoneRow}>
                            <input
                              id="phone1"
                              className={styles.textInput}
                              placeholder="010"
                              maxLength={3}
                              {...register('phone1')}
                            />
                            <input
                              className={styles.textInput}
                              placeholder="0000"
                              maxLength={4}
                              {...register('phone2')}
                            />
                            <input
                              className={styles.textInput}
                              placeholder="0000"
                              maxLength={4}
                              {...register('phone3')}
                            />
                          </div>
                          {(errors.phone1 || errors.phone2 || errors.phone3) && (
                            <p className={styles.fieldError}>
                              {errors.phone1?.message || errors.phone2?.message || errors.phone3?.message}
                            </p>
                          )}
                        </div>
                        <div className={`${styles.field} ${styles.full}`}>
                          <label htmlFor="password">
                            주문 비밀번호 <span className={styles.req}>*</span>
                          </label>
                          <input
                            id="password"
                            type="password"
                            className={styles.textInput}
                            placeholder="견적 문의 시 설정한 비밀번호"
                            {...register('password')}
                          />
                          {errors.password && <p className={styles.fieldError}>{errors.password.message}</p>}
                        </div>
                      </div>
  
                      {notFound && (
                        <div className={`${styles.errBox} ${styles.show}`}>
                          <svg viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                          <span>조회 결과가 없습니다. 입력하신 정보를 다시 확인해주세요.</span>
                        </div>
                      )}
                      {serverError && (
                        <div className={`${styles.errBox} ${styles.show}`}>
                          <svg viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                          <span>{serverError}</span>
                        </div>
                      )}
  
                      <div className={styles.formActions}>
                        <button type="submit" className={styles.btnPrimary} disabled={isSubmitting}>
                          {isSubmitting ? '조회 중...' : '주문 조회하기'}
                          {!isSubmitting && (
                            <svg viewBox="0 0 24 24">
                              <path d="M5 12h14" />
                              <path d="m13 6 6 6-6 6" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <p className={styles.remaining}>남은 조회 시도 횟수: {MAX_FAIL_COUNT - failCount}회</p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* RESULT */}
      {result && (
        <section className={styles.result}>
          {/* 주문 요약: 위(주문번호 + 상태 배지) / 아래(주문일·주문자·연락처 3칸) */}
          <div className={styles.summaryCard}>
            <div className={styles.summaryTop}>
              <div className={styles.summaryTitle}>
                <span className={styles.summaryEyebrow}>주문 정보</span>
                <strong className={styles.summaryOrderNo}>{result.orderNo}</strong>
              </div>
              <div className={styles.summaryBadges}>
                <span className={`${styles.badge} ${styles.badgeStatus}`}>
                  <span className={styles.dot} />
                  {ORDER_STATUS_LABEL[result.status]}
                </span>
                {result.workStatus && (
                  <span className={`${styles.badge} ${WORK_BADGE_CLASS[result.workStatus]}`}>
                    <span className={styles.dot} />
                    {WORK_STATUS_LABEL[result.workStatus]}
                  </span>
                )}
              </div>
            </div>
            <dl className={styles.summaryGrid}>
              <div className={styles.siItem}>
                <span className={styles.siIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M16 3v4M8 3v4M3 10h18" />
                  </svg>
                </span>
                <div>
                  <dt className={styles.siLabel}>주문일</dt>
                  <dd className={styles.siValue}>{formatDate(result.createdAt)}</dd>
                </div>
              </div>
              <div className={styles.siItem}>
                <span className={styles.siIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21a8 8 0 0 1 16 0" />
                  </svg>
                </span>
                <div>
                  <dt className={styles.siLabel}>주문자</dt>
                  <dd className={styles.siValue}>{result.customerName}</dd>
                </div>
              </div>
              <div className={styles.siItem}>
                <span className={styles.siIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
                  </svg>
                </span>
                <div>
                  <dt className={styles.siLabel}>연락처</dt>
                  <dd className={styles.siValue}>{maskPhone(result.phone)}</dd>
                </div>
              </div>
            </dl>
          </div>

          {result.workStatus === 'COMPLETED' && (
            <div className={styles.notice}>
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              <span>작업이 완료되었습니다. 완료 시각: {formatDateTime(result.workCompletedAt)}</span>
            </div>
          )}

          {result.status === 'CANCELLED' ? (
            <div className={styles.notice}>
              <svg viewBox="0 0 24 24">
                <path d="M12 2 4 21h16z" />
                <path d="M12 9v6" />
                <circle cx="12" cy="18" r=".6" fill="currentColor" stroke="none" />
              </svg>
              <span>취소된 주문입니다. 취소 · 환불 관련 문의는 1:1 상담으로 남겨주세요.</span>
            </div>
          ) : (
            <div className={styles.stepper}>
              <h3 className={styles.stepperTitle}>
                {/* 제작 진행 상태 아이콘 (상자 + 진행 화살표) */}
                <span className={styles.stepperIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M21 8 12 3 3 8v8l9 5 9-5V8z" />
                    <path d="m3 8 9 5 9-5M12 13v8" />
                  </svg>
                </span>
                <span>
                  현재 <em>{ORDER_STATUS_LABEL[result.status]}</em> 단계입니다
                </span>
              </h3>
              <div className={styles.stepRow}>
                <div
                  className={styles.progress}
                  style={{ width: `${(stageIndex / (STAGES.length - 1)) * 100}%` }}
                />
                {STAGES.map((stage, i) => (
                  <div
                    key={stage.label}
                    className={`${styles.step} ${i < stageIndex ? styles.done : ''} ${i === stageIndex ? styles.current : ''}`}
                  >
                    <div className={styles.stepCircle}>
                      {i < stageIndex ? (
                        <svg viewBox="0 0 24 24">
                          <polyline points="20 6 9 17 4 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <div className={styles.stepEyebrow}>{stage.eyebrow}</div>
                    <div className={styles.stepName}>{stage.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasGoods && (
            <div className={styles.goodsCard}>
              <h3 className={styles.goodsTitle}>
                {/* 주문 상품 아이콘 (문서 + 체크) */}
                <span className={styles.stepperIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                    <path d="M14 3v5h5" />
                    <path d="m9 14 2 2 4-4" />
                  </svg>
                </span>
                주문 상품
              </h3>

              {/* 라벨 — 값 목록 (값이 없는 항목은 숨김) */}
              <dl className={styles.goodsList}>
                {result.title && (
                  <div className={styles.goodsItem}>
                    <dt>문의 제목</dt>
                    <dd>{result.title}</dd>
                  </div>
                )}
                {result.shape && (
                  <div className={styles.goodsItem}>
                    <dt>제품</dt>
                    <dd>{result.shape}</dd>
                  </div>
                )}
                {result.boxSize && (
                  <div className={styles.goodsItem}>
                    <dt>사이즈</dt>
                    <dd>{result.boxSize}</dd>
                  </div>
                )}
                {typeof result.quantity === 'number' && (
                  <div className={styles.goodsItem}>
                    <dt>수량</dt>
                    <dd>{result.quantity.toLocaleString('ko-KR')}개</dd>
                  </div>
                )}
                {typeof result.unitPrice === 'number' && result.unitPrice > 0 && (
                  <div className={styles.goodsItem}>
                    <dt>개당 단가</dt>
                    <dd>{formatWon(result.unitPrice)}</dd>
                  </div>
                )}
              </dl>

              {/* 결제 금액 강조 */}
              <div className={styles.goodsTotal}>
                <span>결제 금액</span>
                {typeof result.totalPrice === 'number' && result.totalPrice > 0 ? (
                  <strong>{formatWon(result.totalPrice)}</strong>
                ) : (
                  <strong className={styles.goodsPending}>견적 산정 중</strong>
                )}
              </div>
            </div>
          )}

          <OrderTermsInfo
            orderId={result.id}
            shippingMethod={result.shippingMethod}
            paymentTerms={result.paymentTerms}
            paymentDueDate={result.paymentDueDate}
            paidAt={result.paidAt}
            totalPrice={result.totalPrice}
          />

          {result.awaitingPayment && typeof result.totalPrice === 'number' && (
            <PaymentPanel
              orderNo={result.orderNo}
              orderName={result.shape ? `${result.shape} 제작` : '주문 결제'}
              amount={result.totalPrice}
              customerName={result.customerName}
            />
          )}

          {/* 배송 조회 (공용 컴포넌트) — 아래 안내 문구와 간격을 두기 위해 감쌈 */}
          <div className={styles.trackingWrap}>
            <TrackingPanel
              orderId={result.id}
              proofToken={result.proofToken}
              carrier={result.carrier}
              trackingNumber={result.trackingNumber}
            />
          </div>

          <div className={styles.notice}>
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>
              시안 확인, 제작 일정 변경, 취소 · 환불 관련 문의는 아래 <strong>1:1 상담</strong>에서 남겨주시면 담당자가
              확인 후 안내해드립니다.
            </span>
          </div>

          <div className={styles.resultActions}>
            <button type="button" className={`${styles.reBtn} ${styles.reBtnLight}`} onClick={resetLookup}>
              <svg viewBox="0 0 24 24">
                <path d="M3 12a9 9 0 1 0 3-6.7" />
                <path d="M3 4v5h5" />
              </svg>
              다시 조회하기
            </button>
            <Link to={`/mypage/proof/${result.id}`} className={`${styles.reBtn} ${styles.reBtnPrimary}`}>
              1:1 상담 · 시안 확인
              <svg viewBox="0 0 24 24">
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </Link>
            <Link to="/quote" className={`${styles.reBtn} ${styles.reBtnPrimary}`}>
              새 견적 문의
              <svg viewBox="0 0 24 24">
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </Link>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className={styles.cta}>
        <span className={styles.ctaEyebrow}>NEED HELP?</span>
        <h2 className={styles.ctaTitle}>조회에 어려움이 있으신가요?</h2>
        <p className={styles.ctaDesc}>주문번호나 비밀번호를 잊으셨다면 1:1 상담으로 빠르게 확인 가능합니다.</p>
        <div className={styles.ctaActions}>
          <a className={styles.btnLight} href="tel:031-925-6547">
            전화 문의
          </a>
          <Link className={styles.btnSolid} to="/quote">
            견적 문의하기 →
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Lookup;
